import { Controller } from 'stimulus'
import accessibleAutocomplete from 'accessible-autocomplete'
import 'accessible-autocomplete/dist/accessible-autocomplete.min.css'

// Turns the <select> inside this element into an accessible search/dropdown
// combobox (GOV.UK's accessible-autocomplete: ARIA combobox + listbox, arrow
// keys, Enter/Esc, result count announced to screen readers). The <select>
// stays in the page, hidden, as the value that's submitted - and as the
// fallback without JavaScript. Its blank option means "none": the combobox
// shows that as an empty field with a placeholder, and a clear (×) button
// empties it.
//
//   .picker{ data: { controller: "combobox", "combobox-version-value": <changes per render>,
//                    "combobox-placeholder-value": "...", "combobox-clear-label-value": "..." } }
//     %label{ for: "<select id>" } ...
//     %select ...
export default class extends Controller {
  static values = { version: String, placeholder: String, clearLabel: String }

  // Runs on connect, and again when a reflex morphs in new options (and a new
  // version), which also removes the previous combobox
  versionValueChanged() {
    this.enhance()
  }

  enhance() {
    this.select = this.element.querySelector('select')
    if (!this.select) return

    this.element.querySelectorAll('.combobox__field, .combobox__clear').forEach((el) => el.remove())

    // What enhanceSelectElement would do, but starting "none" as an empty
    // field rather than the blank option's text: hide the <select>, give its
    // id to the combobox input (so its <label> labels the combobox)
    const id = this.select.dataset.comboboxId || this.select.id
    this.select.dataset.comboboxId = id
    this.select.id = `${id}-select`
    this.select.hidden = true
    const choices = [...this.select.options].filter((o) => o.value)
    const selected = choices.find((o) => o.selected)

    const field = this.field = document.createElement('div')
    field.className = 'combobox__field'
    this.select.before(field)

    accessibleAutocomplete({
      element: field,
      id,
      source: choices.map((o) => o.text),
      defaultValue: selected ? selected.text : '',
      showAllValues: true,          // dropdown arrow lists everything
      displayMenu: 'overlay',
      minLength: 0,
      placeholder: this.placeholderValue,
      tNoResults: () => 'No matches',
      onConfirm: (label) => this.confirm(label)
    })

    this.input = field.querySelector('.autocomplete__input')
    this.linkDescription()
    this.addClearButton()
    this.input?.addEventListener('input', () => this.updateClearButton())
  }

  // Moves the <select>'s hint (aria-describedby) onto the combobox input,
  // alongside accessible-autocomplete's own usage hint
  linkDescription() {
    const hint = this.select.getAttribute('aria-describedby')
    if (!hint || !this.input) return
    const ids = (this.input.getAttribute('aria-describedby') || '').split(' ').filter(Boolean)
    if (!ids.includes(hint)) this.input.setAttribute('aria-describedby', [hint, ...ids].join(' '))
  }

  // Sets the hidden <select> from the chosen option. An empty field means "none".
  confirm(label) {
    const option = [...this.select.options].find((o) => o.value && o.text === label)
    if (option) {
      this.select.value = option.value
    } else if (this.input && this.input.value.trim() === '') {
      this.select.value = ''
    }
    this.select.dispatchEvent(new Event('change', { bubbles: true }))
    // accessible-autocomplete sets the field's text after this callback
    setTimeout(() => this.updateClearButton())
  }

  addClearButton() {
    this.clearButton = document.createElement('button')
    this.clearButton.type = 'button'
    this.clearButton.className = 'combobox__clear'
    this.clearButton.setAttribute('aria-label', this.clearLabelValue || 'Clear')
    this.clearButton.innerHTML = '<i class="mdi mdi-close" aria-hidden="true"></i>'
    this.clearButton.addEventListener('click', () => this.clear())
    // Inside the field, so it's positioned on the input whatever's above it
    this.field.appendChild(this.clearButton)
    this.updateClearButton()
  }

  // Empties the field (= none) and puts focus back in it, ready to type
  clear() {
    this.select.value = ''
    this.select.dispatchEvent(new Event('change', { bubbles: true }))
    this.setText('')
    this.input.focus()
    this.updateClearButton()
  }

  // Sets the field's text and tells accessible-autocomplete, as if typed
  setText(text) {
    if (!this.input) return
    Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set.call(this.input, text)
    this.input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  // The × replaces the dropdown arrow while there's text
  updateClearButton() {
    if (!this.clearButton || !this.input) return
    const hasValue = this.input.value !== ''
    this.clearButton.hidden = !hasValue
    this.element.classList.toggle('combobox--has-value', hasValue)
  }
}
